import type { DefaultValues, SubmitHandler } from 'react-hook-form';
import { forwardRef } from 'react';

import type { IContentBlockFormBodyProps } from '~/components/molecules/ContentBlock';
import type { IContainerProps } from '~/components/utils/Container';
import type { IFormMode } from '~/helpers/types';
import type { IApiAppPlatform } from '~/lib/api/modules/Apps';
import type {
  IUserBadgeAccess,
  IUserBadgeManagement,
} from '~/lib/api/modules/UserBadges';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { usePersistentForm } from '~/components/utils/PersistentForm';
import { useFormAutoFocus } from '~/hooks/useFormAutoFocus';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { UserBadgeFormMainSection } from './UserBadgeFormMainSection';

export type IUserBadgeFormValues = {
  access?: IUserBadgeAccess;
  color?: string;
  description?: string;
  isDefault?: boolean;
  management?: IUserBadgeManagement;
  name?: string;
  privacyPolicyUrl?: string;
  productId?: string;
  subscriptionUrl?: string;
};

export type IAppFormProps = IContainerProps &
  Omit<
    IContentBlockFormBodyProps<IUserBadgeFormValues>,
    'form' | 'children' | 'onSubmit' | 'submitting'
  > & {
    mode: IFormMode;
    defaultValues?: DefaultValues<IUserBadgeFormValues>;
    onSubmit?: SubmitHandler<IUserBadgeFormValues>;
    appId: string;
  };

export const UserBadgeForm = forwardRef<HTMLFormElement, IAppFormProps>(
  function UserBadgeForm(props, ref) {
    const { sx, mode, defaultValues, onSubmit, appId, ...other } = props;
    const form = usePersistentForm<IUserBadgeFormValues>({
      defaultValues,
    });
    useFormAutoFocus({ form, name: 'name' });
    const appQuery = useAppQuery({ appId });
    const compatiblePlatforms = appQuery.data?.builds
      ? Object.keys(appQuery.data.builds).filter(
          (platform) =>
            appQuery.data.builds?.[platform as IApiAppPlatform]?.iapEnabled,
        )
      : undefined;
    const iapEnabled = compatiblePlatforms && compatiblePlatforms.length > 0;

    return (
      <ContentBlock.FormBody
        ref={ref}
        sx={sx}
        form={form}
        onSubmit={onSubmit}
        {...other}
      >
        <UserBadgeFormMainSection
          form={form}
          mode={mode}
          iapEnabled={iapEnabled}
          appId={appId}
        />
      </ContentBlock.FormBody>
    );
  },
);
